import React from 'react';
import { createPortal } from 'react-dom';
import { TOOLTIP_GAP } from './tooltipConsts';

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  gap?: number;
  // horizontal offset in pixels (positive moves tooltip right)
  offsetX?: number;
}

const Tooltip: React.FC<TooltipProps> = ({ text, children, gap, offsetX }) => {
  const id = React.useId();
  const [visible, setVisible] = React.useState(false);
  // include placement so we can flip tooltip to the bottom when there's no room above
  const [pos, setPos] = React.useState<{ left: number; top: number; placement: 'top' | 'bottom' }>({ left: 0, top: 0, placement: 'top' });
  const childRef = React.useRef<HTMLElement | null>(null);

  const show = (el?: HTMLElement | null) => {
    try {
      const target = el || childRef.current;
      if (!target) {
        setVisible(true);
        return;
      }
      const rect = target.getBoundingClientRect();
      const gapVal = gap ?? TOOLTIP_GAP;

      // Horizontal center, apply optional per-tooltip offset, then clamp to viewport.
      // We clamp using an estimated half-width so centered tooltips don't clip at edges.
      const rawLeft = Math.round(rect.left + rect.width / 2) + (offsetX ?? 0);
      const viewportWidth = window.innerWidth || 1024;
      const edgePadding = 8;
      const maxTooltipWidthPx = 384; // aligns with max-w-sm
      const halfEstimated = Math.round(maxTooltipWidthPx / 2);
      const minLeft = edgePadding + halfEstimated;
      const maxLeft = viewportWidth - edgePadding - halfEstimated;
      const left = minLeft <= maxLeft
        ? Math.min(Math.max(rawLeft, minLeft), maxLeft)
        : Math.round(viewportWidth / 2);

      // Choose placement based on available space
      const spaceAbove = rect.top;
      const spaceBelow = (window.innerHeight || 768) - rect.bottom;
      const preferBottom = spaceAbove < 48 && spaceBelow > spaceAbove;
      const placement: 'top' | 'bottom' = preferBottom ? 'bottom' : 'top';

      const top = placement === 'top'
        ? Math.round(rect.top - gapVal)
        : Math.round(rect.bottom + gapVal);

      setPos({ left, top, placement });
      setVisible(true);
    } catch {
      setVisible(true);
    }
  };
  const hide = () => setVisible(false);

  const child = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<any>, {
        ref: (node: any) => {
          // preserve existing ref if present
          const prevRef = (children as any).ref;
          if (typeof prevRef === 'function') prevRef(node);
          else if (prevRef && typeof prevRef === 'object') (prevRef as any).current = node;
          childRef.current = node;
        },
        onMouseEnter: (...args: any[]) => {
          show();
          const fn = (children as any).props.onMouseEnter;
          if (typeof fn === 'function') fn(...args);
        },
        onMouseLeave: (...args: any[]) => {
          hide();
          const fn = (children as any).props.onMouseLeave;
          if (typeof fn === 'function') fn(...args);
        },
        onFocus: (...args: any[]) => {
          show();
          const fn = (children as any).props.onFocus;
          if (typeof fn === 'function') fn(...args);
        },
        onBlur: (...args: any[]) => {
          hide();
          const fn = (children as any).props.onBlur;
          if (typeof fn === 'function') fn(...args);
        },
        'aria-describedby': id,
      })
    : children;

  return (
    <>
      {child}
      {visible && typeof document !== 'undefined' && createPortal(
        <div
          id={id}
          role="tooltip"
          aria-hidden={!visible}
          style={{
            position: 'fixed',
            left: `${pos.left}px`,
            top: `${pos.top}px`,
            transform: pos.placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
            pointerEvents: 'none',
            zIndex: 2147483647,
            maxWidth: 'calc(100vw - 16px)'
          }}
        >
          <div className="inline-block max-w-xs sm:max-w-sm bg-blue-500 text-white text-[12px] rounded px-2 py-1 shadow-lg whitespace-pre-wrap break-words dark:bg-blue-600 dark:text-white">
            {text}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Tooltip;
